import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { missionsApi, findingsApi } from '@/api/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Shield, Search, Filter, FileCode, Download } from 'lucide-react'

export function FindingsPage() {
  // Note: useAuth is called to ensure auth state is initialized
  useAuth()
  const [searchParams] = useSearchParams()
  const missionId = searchParams.get('mission')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedMission, setSelectedMission] = useState(missionId || '')

  const { data: missions = [] } = useQuery({
    queryKey: ['missions'],
    queryFn: () => missionsApi.list(),
  })

  const { data: findings = [], isLoading } = useQuery({
    queryKey: ['findings', selectedMission],
    queryFn: () => findingsApi.list(selectedMission),
    enabled: !!selectedMission,
  })

  const filteredFindings = findings.filter(
    (f) =>
      f.cwe_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.scanner_identity.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.file_path?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const getSeverityVariant = (label: string) => {
    const lower = label.toLowerCase()
    if (lower === 'critical') return 'critical'
    if (lower === 'high') return 'high'
    if (lower === 'medium') return 'medium'
    return 'low'
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold">Findings</h2>
          <p className="text-muted-foreground">
            View and manage software assurance findings across missions
          </p>
        </div>
        <Button variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <Select
          value={selectedMission || undefined}
          onValueChange={setSelectedMission}
        >
          <SelectTrigger className="w-[250px]">
            <SelectValue placeholder="Select Mission" />
          </SelectTrigger>
          <SelectContent>
            {missions.map((m) => (
              <SelectItem key={m.mission_id} value={m.mission_id}>
                {m.mission_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by CWE, scanner, or file path..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <Button variant="outline">
          <Filter className="h-4 w-4 mr-2" />
          Filters
        </Button>
      </div>

      {/* Findings table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Findings ({filteredFindings.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!selectedMission ? (
            <div className="text-center py-12 text-muted-foreground">
              <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Select a mission to view findings</p>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>CWE</th>
                    <th>JFAC Score</th>
                    <th>Severity</th>
                    <th>Scanner</th>
                    <th>File Location</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFindings.map((finding) => (
                    <tr key={finding.finding_id} className="cursor-pointer">
                      <td>
                        <a
                          href={`https://cwe.mitre.org/data/definitions/${finding.cwe_id.replace('CWE-', '')}.html`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-primary hover:underline"
                        >
                          {finding.cwe_id}
                        </a>
                      </td>
                      <td>
                        <span className="font-mono font-bold">
                          {finding.jfac_risk.score.toFixed(2)}
                        </span>
                      </td>
                      <td>
                        <Badge variant={getSeverityVariant(finding.jfac_risk.label)}>
                          {finding.jfac_risk.label}
                        </Badge>
                      </td>
                      <td className="text-muted-foreground">
                        {finding.scanner_identity}
                      </td>
                      <td>
                        {finding.file_path ? (
                          <div className="flex items-center gap-2">
                            <FileCode className="h-4 w-4 text-muted-foreground" />
                            <span className="font-mono text-xs">
                              {finding.file_path}
                              {finding.line_number && `:${finding.line_number}`}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="text-muted-foreground text-xs">
                        {new Date(finding.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredFindings.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  No findings match your search criteria.
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
